import '../../styles/interactives.scss'
import { useCallback, useEffect, useRef } from 'react'
const threshold = [0.65, 0.85]

export default function OrganicExposure() {
  const paragraphs = useRef(null)
  const interactive = useRef(null)

  const handleSteps = useCallback(
    (entries) => {
      entries.forEach((entry) => {
        // check that we have a ref, and that we are measuring one way only
        if (!interactive?.current || entry?.boundingClientRect.y < 0) return
        const ratio = entry?.intersectionRatio ?? 0

        // On Mount the observer get called with a 0 value
        if (ratio === 0) return

        const paragraph = Number(entry?.target.getAttribute('data-index') ?? 0)
        if (paragraph === 0) return

        const beforeP1 = paragraph === 1 && ratio < threshold[0]
        const afterP1 = paragraph === 1 && ratio > threshold[1]

        const beforeP2 = paragraph === 2 && ratio < threshold[0]
        const afterP2 = paragraph === 2 && ratio > threshold[1]

        if (beforeP1) {
          interactive.current.removeAttribute('data-step')
        } else if (afterP1 || beforeP2) {
          interactive.current.setAttribute('data-step', 1)
        } else if (afterP2) {
          interactive.current.setAttribute('data-step', 2)
        }
      })
    },
    [interactive]
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleSteps, { threshold })

    if (paragraphs?.current) {
      paragraphs.current.querySelectorAll('li').forEach((paragraph, index) => {
        paragraph.setAttribute('data-index', index + 1)
        observer.observe(paragraph)
      })
    }

    return () => observer.disconnect()
  }, [handleSteps, paragraphs])

  return (
    <section className="interactive" name="Organic Exposure">
      <header>
        <h2>
          Our data indicates that{' '}
          <strong> &ldquo;organic&rdquo; exposure</strong> plays an outsized
          role in how first-gen students make career decisions
        </h2>
      </header>
      <main>
        <aside>
          <figure ref={interactive} className="organic-exposure">
            <h4>
              <strong>Low Exposure likelihood</strong> per Industry Interest
            </h4>
            <ul>
              <li>Higher than average</li>
              <li>Lower than average</li>
              <li>Average</li>
            </ul>
            <ol>
              <li title="Healthcare">
                <span>+41%</span>
              </li>
              <li title="Teaching">
                <span>+28%</span>
              </li>
              <li title="Nonprofits">
                <span>+23%</span>
              </li>
              <li title="Government">
                <span>+5%</span>
              </li>
              <li title="Consulting">
                <span>-18%</span>
              </li>
              <li title="Finance">
                <span>-31%</span>
              </li>
            </ol>
          </figure>
        </aside>
        <ul ref={paragraphs}>
          <li>
            <p>
              The top industries selected by first-gen students are the ones
              they <strong>come in contact with regularly </strong>
            </p>
          </li>
          <li>
            <p>
              And their <strong>aspirations are inherently limited</strong> by
              less exposure to a wide range of professions, like finance and
              consulting
            </p>
          </li>
        </ul>
      </main>
    </section>
  )
}
